import { LOCAL_STORAGE_KEYS } from "@/constants/storageKey";
import type { CacheItem, CacheState } from "@/store/cacheReducer";
import type { ProductsQuery } from "@/viewModels/shop/features/useFilter/types";

export const CACHE_CONFIG = {
  categories: { key: "categories", expiry: 24 * 60 * 60 * 1000 }, // 1D
  roastTerms: { key: "roastTerms", expiry: 24 * 60 * 60 * 1000 }, // 1D
  productDetail: {
    keyPrefix: "productDetail",
    expiry: 1 * 60 * 60 * 1000, // 1H
  },
  products: {
    keyPrefix: "products",
    expiry: 1 * 60 * 60 * 1000, // 1H
  },
} as const;

/**
 * 檢查快取項目是否有效（尚未過期）
 */
export const isCacheValid = (item: CacheItem | undefined): boolean => {
  if (!item) return false;
  return Date.now() - item.lastFetched < item.expiry;
};

/**
 * 從快取狀態中取得有效的快取資料
 * @param key - 快取項目的 key
 * @param cache - Redux store 內的快取狀態
 * @returns 快取資料（有效時），否則回傳 null
 */
export const getValidCacheData = <T>(key: string, cache: CacheState): T | null => {
  const item = cache[key];
  return isCacheValid(item) ? (item.data as T) : null;
};

/**
 * 從 localStorage 讀取整體快取，主要在初始化 Redux store 時使用
 * @returns {CacheState} - 回傳從儲存取出的快取物件，若不存在則為空物件
 */
export const restoreCacheFromStorage = (): CacheState => {
  const cache = localStorage.getItem(LOCAL_STORAGE_KEYS.appCache);

  if (cache) {
    const parsedCache = JSON.parse(cache) as CacheState;
    const validCache = clearExpiredCaches(parsedCache);
    return validCache;
  }
  return {};
};

/**
 * 清理過期的快取
 */
const clearExpiredCaches = (cache: CacheState): CacheState => {
  const clearedCache: CacheState = {};
  for (const key in cache) {
    if (isCacheValid(cache[key])) {
      clearedCache[key] = cache[key];
    }
  }
  return clearedCache;
};

/**
 * 產生商品詳情 cache key
 */
export const buildProductDetailCacheKey = (productSlug: string) =>
  `${CACHE_CONFIG.productDetail.keyPrefix}|${productSlug}`;

/**
 * 產生商品列表 cache key
 * 根據傳入的 filter 產生唯一鍵值
 * 範例格式：products|category=x&attr=淺焙,attr=半磅&orderby=price&order=asc
 */
export const buildProductsCacheKey = (filter: ProductsQuery) => {
  const items: string[] = [];

  const { category, attributes, orderby, order } = filter;

  if (category) {
    items.push(`category=${category}`);
  }

  if (!!attributes?.length) {
    const attrs = attributes.map((attr) => `attr=${attr.slug}`);
    items.push(attrs.join(",")); // eg. attr=attr1,attr=attr2
  }

  if (orderby) {
    items.push(`orderby=${orderby}`);
  }

  if (order) {
    items.push(`order=${order}`);
  }

  const cacheKey = `${CACHE_CONFIG.products.keyPrefix}|${items.join("&")}`;
  return cacheKey;
};
